<template>
  <BaseDrawer
    :is-open="isOpen"
    @close="closeShoppingCartDrawer"
  >
    <template #header>
      <DrawerHeader @close="closeShoppingCartDrawer">
        <div class="flex items-center justify-between">
          <div
            class="h-full text-xl font-bold leading-tight"
          >
            Order
            <span
              v-if="currentOrder.orderNumber"
              class="text-background-fg/40"
            >
              #{{ currentOrder.orderNumber }}
            </span>
          </div>
          <button
            type="button"
            class="flex items-center justify-center rounded-lg border-error bg-error/5 px-2 text-base text-error"
            @click="clearCurrentOrder"
          >
            Clear Cart
          </button>
        </div>
      </DrawerHeader>
    </template>
    <button
      class="mx-4 mb-3 flex items-center justify-between rounded-md bg-background-fg/5 p-1.5"
      @click="showConfigureOrderDialog"
    >
      <IconCog class="absolute h-5 text-background-fg/70" />
      <span class="w-full px-6 text-center">
        Configure Order
      </span>
    </button>
    <div class="flex-1 overflow-y-auto">
      <div
        v-if="currentOrder.orderItems.length > 0"
        class="divide-y divide-background-fg/5 border-y border-background-fg/5"
      >
        <div
          v-for="(item, index) in currentOrder.orderItems"
          :key="index"
        >
          <ShoppingCartItem
            :item="item"
            @click="onEditItem(index)"
          />
        </div>
      </div>
      <div class="mt-1.5 text-center">
        <button
          class="rounded-full bg-background-fg/5 px-3 py-1 text-sm text-background-fg/60"
          @click="closeShoppingCartDrawer"
        >
          Add Items
        </button>
      </div>
    </div>
    <div
      class="space-y-2 border-t border-background-fg/5 px-4 pb-4 pt-2"
    >
      <div>
        <div class="flex items-center justify-between">
          <div class="text-xs text-background-fg/70">
            Total
            <span>
              ({{ totalQuantity }}
              {{ totalQuantity === 1 ? "item" : "items" }})
            </span>
          </div>
          <div class="text-sm font-semibold">
            {{
              formatToMerchantCurrencyString(
                totalOrderItemCostPence
              )
            }}
          </div>
        </div>
        <div
          v-if="!!serviceChargeInPence"
          class="flex items-center justify-between"
        >
          <div class="text-xs text-background-fg/70">
            Service Charge
          </div>
          <div class="text-sm font-semibold">
            {{
              formatToMerchantCurrencyString(
                serviceChargeInPence
              )
            }}
          </div>
        </div>
        <div
          v-if="currentOrder?.createdAt"
          class="flex items-center justify-between"
        >
          <div class="text-xs text-background-fg/70"
            >Start Time</div
          >
          <div class="text-sm font-semibold">
            <span
              v-if="
                !DateFns.isToday(currentOrder.createdAt)
              "
            >
              {{
                DateFns.format(
                  currentOrder.createdAt,
                  "MM/dd"
                )
              }}
            </span>
            {{ formatToTime(currentOrder.createdAt) }}
          </div>
        </div>
      </div>
      <ActionButtonPrimary
        class="w-full py-3"
        :disabled="totalOrderItemCostPence === 0"
        @click="handleClickPay"
      >
        <div
          class="flex flex-row items-center justify-center whitespace-nowrap font-semibold"
        >
          Pay
          {{
            formatToMerchantCurrencyString(
              totalCostWithServiceChargeInPence
            )
          }}
        </div>
      </ActionButtonPrimary>
    </div>

    <!-- We don't want to use drawer state to open/close as want a full component unmount -->
    <template v-if="prePaymentOrderResponse">
      <PaymentDrawer
        :invoice="prePaymentOrderResponse.invoice"
        :order-id="prePaymentOrderResponse.id"
        :order-open-status="
          prePaymentOrderResponse.openStatus
        "
        @close="prePaymentOrderResponse = null"
        @close-and-clear-order="
          onClosePaymentDrawerAndClearOrder
        "
      />
    </template>

    <OrderItemDrawer ref="orderItemModalRef" />

    <ConfigureOrderDrawer
      :is-open="congiureOrderDialogIsOpen"
      @close="hideConfigureOrderDialog"
    />
  </BaseDrawer>
</template>

<script setup lang="ts">
import PaymentDrawer from "./PaymentDrawer.vue"
import ConfigureOrderDrawer from "@/components/ConfigureOrderDrawer.vue"

import { useDialogState } from "@/composables/useDialogState"
import type { CreateUpdateOrderResponse } from "@/services/sharedTypes/orders"

const { formatToMerchantCurrencyString } = useCurrency()

defineProps<{
  isOpen: boolean
}>()

const { startLoading, stopLoading } =
  usePageBlockingLoading()
const {
  totalOrderItemCostPence,
  totalQuantity,
  currentOrder,
  updateItem,
  saveCurrentOrder,
  clearCurrentOrder,
} = useCurrentOrder()
const { close: closeShoppingCartDrawer } =
  useShoppingCartDrawer()
const {
  isOpen: congiureOrderDialogIsOpen,
  hideDialog: hideConfigureOrderDialog,
  showDialog: showConfigureOrderDialog,
} = useDialogState()

const serviceChargeInPence = computed(() =>
  Math.max(
    Math.round(
      totalOrderItemCostPence.value *
        ((currentOrder.value.serviceChargePercentage ?? 0) /
          100)
    ) + (currentOrder.value.serviceChargeFlat ?? 0),
    -totalOrderItemCostPence.value
  )
)

const totalCostWithServiceChargeInPence = computed(
  () =>
    serviceChargeInPence.value +
    totalOrderItemCostPence.value
)

const prePaymentOrderResponse =
  ref<CreateUpdateOrderResponse | null>(null)
const handleClickPay = async () => {
  startLoading()
  try {
    prePaymentOrderResponse.value = await saveCurrentOrder()
  } catch {
    toast.error("Something went wrong")
  } finally {
    stopLoading()
  }
}

const onClosePaymentDrawerAndClearOrder = () => {
  prePaymentOrderResponse.value = null
  clearCurrentOrder()
  closeShoppingCartDrawer()
}

const orderItemModalRef = useTemplateRef(
  "orderItemModalRef"
)

const onEditItem = async (itemIndex: number) => {
  if (!orderItemModalRef.value) return

  const { isCanceled, data } =
    await orderItemModalRef.value.reveal({
      orderItemIndex: itemIndex,
    })
  if (isCanceled || !data) return

  updateItem(itemIndex, data.params)
}
</script>
